import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogContentComponent } from '../../content/common/dialog/dialog-content/dialog-content.component';
import { DialogParams } from '../models/dialog-params';
import { BehaviorSubject, Observable } from 'rxjs';
import { SignUpDialogComponent } from '../../content/public/sign-up-dialog/sign-up-dialog.component';
import { CertificateViewComponent } from '../../content/private/certificates/components/certificate-view/certificate-view.component';
import { Certificate } from '../models/certificate';
import { CertificateCreateRequest } from '../models/certificate-create-request';
import { ReleaseDialogComponent } from '../../content/private/certificates/components/release-dialog/release-dialog.component';
import { EnterPasswordDialogComponent } from '../../content/private/profile/dialogs/enter-password-dialog/enter-password-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { TermsAgreeDialogComponent } from '../../content/public/terms-agree-dialog/terms-agree-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryParamsKeys } from '../enums';
import { CertHistoryDialogComponent } from '../../content/private/certificates/components/cert-history-dialog/cert-history-dialog.component';
import { OnboardingService } from './api/onboarding.service';
import { CertificateCloneDialogComponent } from '../../content/private/certificates/components/certificate-clone-dialog/certificate-clone-dialog.component';
import { navigateToLoginWithNext } from '../utils';
import { ProgressDialogComponent } from '../../content/common/dialog/progress-dialog/progress-dialog.component';
import { CreateWhitelistRuleDialogComponent } from '../../content/private/profile/dialogs/create-whitelist-rule-dialog/create-whitelist-rule-dialog.component';
import { CertParseDialogComponent } from '../../content/private/certificates/components/cert-parse-dialog/cert-parse-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(
    private matDialog: MatDialog,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private onboardingService: OnboardingService
  ) {
    this.currentDialog$.subscribe((dialog) => {
      if (dialog) {
        const existingTour = this.onboardingService.currentAvailableTour.value;
        this.onboardingService.pauseCurrentUntil(dialog.afterClosed());
        dialog.afterClosed().subscribe(() => {
          this.onboardingService.registerCurrentAvailableTour(existingTour);
        });
      }
    });
  }

  private _currentDialog?:
    | MatDialogRef<DialogContentComponent>
    | MatDialogRef<ReleaseDialogComponent>
    | MatDialogRef<CertificateCloneDialogComponent>
    | MatDialogRef<CertHistoryDialogComponent>;

  public currentDialog$ = new BehaviorSubject<MatDialogRef<any> | null>(null);

  open<T>(params: DialogParams<T>): { result: Observable<any> } {
    this._currentDialog = this.matDialog.open(DialogContentComponent, {
      disableClose: params.disableClose,
      data: { ...params },
    });
    this.currentDialog$.next(this._currentDialog);
    this._currentDialog
      .afterClosed()
      .subscribe(() => this.currentDialog$.next(null));
    return {
      result: this._currentDialog.afterClosed(),
    };
  }

  openProgressDialog(data: ProgressDialogData) {
    return this.open({
      component: ProgressDialogComponent,
      disableClose: true,
      data,
    });
  }

  openSignUpDialog(data?: { name: string; email: string }) {
    return this.open({
      component: SignUpDialogComponent,
      title: this.translateService.instant('dialogs.signUp.title'),
      data,
    });
  }

  openCertificateView(
    certificate: string | Certificate | CertificateCreateRequest
  ) {
    return this.open({
      component: CertificateViewComponent,
      data: { certificate },
    });
  }

  closeCurrent(dialogResult: any): void {
    this._currentDialog?.close(dialogResult);
    this._currentDialog = undefined;
  }

  closeAll(): Observable<void> {
    this._currentDialog = undefined;
    this.matDialog.closeAll();
    return this.matDialog.afterAllClosed;
  }

  openReleaseDialog(data: any): Observable<any> {
    this._currentDialog = this.matDialog.open(ReleaseDialogComponent, {
      data,
      disableClose: true,
      panelClass: 'ling-dialog-no-padding',
    });
    this.currentDialog$.next(this._currentDialog);
    this._currentDialog
      .afterClosed()
      .subscribe(() => this.currentDialog$.next(null));
    return this._currentDialog.afterClosed();
  }

  openConfirmPasswordDialog(): { result: Observable<string | false> } {
    return this.open({
      title: this.translateService.instant('dialogs.enterPassword.title'),
      component: EnterPasswordDialogComponent,
    });
  }

  openNotInWhiteListDialog() {
    this.open({
      title: this.translateService.instant('dialogs.notInWhiteList.title'),
      text: this.translateService.instant('dialogs.notInWhiteList.text'),
      confirmButtonText: this.translateService.instant(
        'dialogs.notInWhiteList.confirm'
      ),
    }).result.subscribe((result) => {
      if (result) {
        window.open('https://www.lingon.io/sign-up', '_blank')!.focus();
      }
    });
  }

  openAccountAlreadyExistsDialog(email: string) {
    this.open({
      title: this.translateService.instant(
        'dialogs.accountAlreadyExists.title'
      ),
      text: this.translateService.instant('dialogs.accountAlreadyExists.text'),
      confirmButtonText: this.translateService.instant(
        'dialogs.accountAlreadyExists.confirm'
      ),
    }).result.subscribe((result) => {
      if (result) {
        this.closeAll();
        navigateToLoginWithNext(this.router, this.route, this.router.url, {
          [QueryParamsKeys.ImportPublicFormsState]: email,
        });
      }
    });
    return this._currentDialog?.afterClosed();
  }

  openTermsAgreementDialog() {
    return this.open({
      title: this.translateService.instant('dialogs.agreementUpdated.title'),
      confirmButtonText: this.translateService.instant(
        'dialogs.agreementUpdated.confirm'
      ),
      rejectButtonText: this.translateService.instant(
        'dialogs.agreementUpdated.reject'
      ),
      disableClose: true,
      component: TermsAgreeDialogComponent,
    }).result;
  }

  openCertificateHistoryDialog(certificate: Certificate) {
    this._currentDialog = this.matDialog.open(CertHistoryDialogComponent, {
      data: {
        certificate,
      },
      panelClass: 'ling-dialog-no-padding',
    });
    return this._currentDialog.afterClosed();
  }

  openCertificateCloneDialog(certificates: Certificate[]) {
    this._currentDialog = this.matDialog.open(CertificateCloneDialogComponent, {
      data: {
        certificates,
      },
    });
    return this._currentDialog.afterClosed();
  }

  openCreateWhitelistRuleDialog(
    isEdit: boolean = false,
    rule?: string
  ): {
    result: Observable<string | false>;
  } {
    const titleToTranslate = isEdit
      ? 'dialogs.createWhitelistRule.editTitle'
      : 'dialogs.createWhitelistRule.createTitle';
    const title = this.translateService.instant(titleToTranslate);

    return this.open({
      title,
      component: CreateWhitelistRuleDialogComponent,
      data: {
        rule,
        isEdit,
      },
    });
  }

  openParseCertificateDialog() {
    return this.open({
      component: CertParseDialogComponent,
    }).result;
  }
}

export type ProgressDialogData = {
  call: Observable<any>;
  isMultiple: boolean;
  delay: number;
  text: {
    single: {
      loading: string;
      success: string;
      failure: string;
    };
    multiple: {
      loading: string;
      success: string;
      failure: string;
    };
  };
};
